import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Mail, Settings2, Users2, Shield, KeyRound, Send, CheckCircle2,
  AlertTriangle, Loader2, RefreshCw, Pencil, Trash2, Eye, EyeOff, Plug,
  Globe, Building2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  configured_ok:   { icon: CheckCircle2,   cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30", label: "Configurado e operacional" },
  configured_untested: { icon: Plug,       cls: "text-amber-400 bg-amber-400/10 border-amber-400/30",       label: "Configurado, aguarde teste" },
  not_configured:  { icon: AlertTriangle,  cls: "text-red-400 bg-red-400/10 border-red-400/30",             label: "Não configurado" },
  error:           { icon: AlertTriangle,  cls: "text-red-400 bg-red-400/10 border-red-400/30",             label: "Erro na integração" },
};

export default function IntegrationsPage() {
  const [me, setMe] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [scope, setScope] = useState("global");
  const [scopeCompany, setScopeCompany] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testTo, setTestTo] = useState("");

  const isMaster = me?.role === "master";

  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([api.get("/auth/me"), api.get("/companies")]);
        setMe(u.data); setCompanies(c.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope === "company" && scopeCompany ? { company_id: scopeCompany } : {};
      const { data } = await api.get("/admin/integrations/email", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [scope, scopeCompany]);

  useEffect(() => { load(); }, [load]);

  const effective = data?.effective || {};
  const healthStatus = useMemo(() => {
    if (!effective.configured) return STATUS_STYLES.not_configured;
    return STATUS_STYLES.configured_ok;
  }, [effective.configured]);
  const HealthIcon = healthStatus.icon;

  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const payload = scope === "company" && scopeCompany ? { company_id: scopeCompany } : {};
      const { data } = await api.post("/admin/integrations/email/test-connection", payload);
      setTestResult({ type: "connection", ...data });
      if (data.ok) toast.success(`Resend HTTP ${data.http_status} — conexão OK`);
      else toast.error(data.message || "Falha na conexão");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Informe um e-mail válido."); return; }
    setSending(true);
    try {
      const payload = { to: testTo, company_id: scope === "company" ? scopeCompany : null };
      const { data } = await api.post("/admin/integrations/email/test-email", payload);
      setTestResult({ type: "send", ...data });
      if (data.ok) toast.success(`E-mail enviado: ${data.resend_id}`);
      else toast.error(data.error || "Falha no envio");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); setShowTestDialog(false); }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#C5A05915] border border-[#C5A05940] flex items-center justify-center">
              <Settings2 className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-[34px] font-semibold tracking-tight">Integrações</h1>
              <p className="text-sm text-[#AAB4A8] mt-1">Credenciais, destinatários do RH e saúde da integração</p>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="integ-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </header>

      {/* Health card */}
      <div className="rounded-2xl border border-[#2C3A2F] bg-gradient-to-br from-[#1A231F] to-[#0F1C14] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${healthStatus.cls}`}>
              <HealthIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059]">Resend · E-mails transacionais</div>
              <div className="font-display text-lg mt-0.5">{healthStatus.label}</div>
              <div className="text-xs text-[#AAB4A8] mt-1">
                Origem: <b className="text-white/90">{effective.origin === "env" ? "Variáveis de ambiente" : effective.origin === "manual_global" ? "Configuração administrativa (global)" : effective.origin === "manual_tenant" ? "Configuração administrativa (empresa)" : "não disponível"}</b>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs min-w-[320px]">
            <Field label="Provider" value="Resend" />
            <Field label="Remetente" value={`${effective.sender_name || "—"} <${effective.sender_email || "—"}>`} />
            <Field label="Site público" value={effective.public_site_url || "—"} mono />
            <Field label="API Key" value={effective.api_key_mask || "—"} mono />
            <Field label="Webhook" value={effective.webhook_secret_configured ? "Configurado" : "Não configurado"} />
            {data?.env && (
              <Field label="Cofre" value={data.env.INTEGRATION_ENCRYPTION_KEY ? "Pronto" : "Indisponível"} />
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-5 flex-wrap">
          <Button variant="outline" size="sm" onClick={testConn} disabled={testing || !effective.configured} data-testid="integ-test-conn">
            {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1.5" />} Testar conexão
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTestDialog(true)} disabled={!effective.configured} data-testid="integ-test-send">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar e-mail de teste
          </Button>
          {isMaster && (
            <Button size="sm" onClick={() => setShowEditor(true)} className="bg-[#C5A059] hover:bg-[#B59152] text-black ml-auto" data-testid="integ-configure">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Configurar integração
            </Button>
          )}
        </div>

        {testResult && (
          <div className="mt-4 rounded-lg border border-[#2C3A2F] bg-[#0B140F] p-3 text-xs font-mono space-y-1">
            {testResult.type === "connection" ? (
              <>
                <div><span className="text-[#AAB4A8]">Resend connection:</span> <span className={testResult.ok ? "text-emerald-400" : "text-red-400"}>{testResult.ok ? "OK" : "ERRO"}</span></div>
                <div><span className="text-[#AAB4A8]">HTTP status:</span> {testResult.http_status || "—"}</div>
                <div><span className="text-[#AAB4A8]">Origem:</span> {testResult.origin}</div>
                <div><span className="text-[#AAB4A8]">Mensagem:</span> {testResult.message}</div>
              </>
            ) : (
              <>
                <div><span className="text-[#AAB4A8]">Resultado:</span> <span className={testResult.ok ? "text-emerald-400" : "text-red-400"}>{testResult.status}</span></div>
                <div><span className="text-[#AAB4A8]">Resend ID:</span> {testResult.resend_id || "—"}</div>
                <div><span className="text-[#AAB4A8]">De:</span> {testResult.from_address || "—"}</div>
                <div><span className="text-[#AAB4A8]">Para:</span> {testResult.to}</div>
                <div><span className="text-[#AAB4A8]">Origem config:</span> {testResult.config_origin}</div>
                <div><span className="text-[#AAB4A8]">Data/hora:</span> {testResult.at}</div>
                {testResult.error && <div><span className="text-red-400">Erro:</span> {testResult.error}</div>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Scope switcher */}
      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] p-3 flex items-center gap-3">
        <Shield className="w-4 h-4 text-[#C5A059]" />
        <span className="text-sm text-[#AAB4A8]">Escopo:</span>
        <Button variant={scope === "global" ? "default" : "outline"} size="sm" onClick={() => { setScope("global"); setScopeCompany(""); }} data-testid="integ-scope-global"><Globe className="w-3.5 h-3.5 mr-1.5" /> Global</Button>
        <Button variant={scope === "company" ? "default" : "outline"} size="sm" onClick={() => setScope("company")} data-testid="integ-scope-company"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Por empresa</Button>
        {scope === "company" && (
          <Select value={scopeCompany} onValueChange={setScopeCompany}>
            <SelectTrigger className="w-72" data-testid="integ-company-select"><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
            <SelectContent>
              {(isMaster ? companies : companies.filter(c => (me?.company_ids || []).includes(c.id))).map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name || c.nome_fantasia || c.razao_social}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="recipients" className="w-full">
        <TabsList className="bg-[#1A231F] border border-[#2C3A2F]">
          <TabsTrigger value="recipients" data-testid="integ-tab-recipients"><Users2 className="w-4 h-4 mr-2" /> Destinatários do RH</TabsTrigger>
          {isMaster && (
            <TabsTrigger value="env" data-testid="integ-tab-env"><KeyRound className="w-4 h-4 mr-2" /> Variáveis de ambiente</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="recipients" className="mt-6">
          <RecipientsPanel scope={scope} companyId={scopeCompany} isMaster={isMaster} companies={companies} />
        </TabsContent>

        {isMaster && (
          <TabsContent value="env" className="mt-6">
            <EnvPanel env={data?.env || {}} />
          </TabsContent>
        )}
      </Tabs>

      {/* Credential editor dialog */}
      <ConfigEditorDialog
        open={showEditor} onOpenChange={setShowEditor}
        scope={scope} companyId={scopeCompany}
        current={scope === "company" ? data?.tenant : data?.global}
        onSaved={load}
      />

      {/* Test-email dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Enviar e-mail de teste</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Enviar teste para</label>
              <Input type="email" placeholder="voce@empresa.com" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="integ-test-to" />
            </div>
            <p className="text-xs text-[#AAB4A8]">O envio usa a configuração atualmente ativa ({effective.origin}). O resultado é registrado em Health Check.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTestDialog(false)}>Cancelar</Button>
            <Button onClick={sendTest} disabled={sending || !testTo} data-testid="integ-test-confirm">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------
const Field = ({ label, value, mono = false }) => (
  <div className="flex items-start gap-2 min-w-0">
    <span className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8] whitespace-nowrap pt-0.5">{label}:</span>
    <span className={`text-xs truncate ${mono ? "font-mono" : ""} text-white/90`}>{value}</span>
  </div>
);

// ---------------------------------------------------------------
function ConfigEditorDialog({ open, onOpenChange, scope, companyId, current, onSaved }) {
  const [form, setForm] = useState({ api_key: "", webhook_secret: "", sender_email: "", sender_name: "", public_site_url: "", enabled: true });
  const [showKey, setShowKey] = useState(false);
  const [showWh, setShowWh] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        api_key: "", webhook_secret: "",
        sender_email: current?.sender_email || "",
        sender_name: current?.sender_name || "",
        public_site_url: current?.public_site_url || "",
        enabled: current?.enabled !== false,
      });
      setShowKey(false); setShowWh(false);
    }
  }, [open, current]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        scope, company_id: scope === "company" ? companyId : null,
        enabled: form.enabled,
        sender_email: form.sender_email,
        sender_name: form.sender_name,
        public_site_url: form.public_site_url,
      };
      if (form.api_key) body.api_key = form.api_key;
      if (form.webhook_secret) body.webhook_secret = form.webhook_secret;
      await api.put("/admin/integrations/email", body);
      toast.success("Configuração salva");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    try {
      await api.delete("/admin/integrations/email/key", { params: { scope, company_id: scope === "company" ? companyId : null } });
      toast.success("Chave removida — voltou ao fallback de ambiente.");
      onSaved?.();
      setConfirmClear(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-[#C5A059]" />
            Configurar integração · {scope === "global" ? "Global" : "Empresa"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-100 flex items-start gap-2">
            <Shield className="w-3.5 h-3.5 mt-0.5" />
            <div>A chave Resend é criptografada no banco com <code className="font-mono">INTEGRATION_ENCRYPTION_KEY</code>, nunca é retornada pela API e nunca aparece em logs. Deixe em branco para manter a atual.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Provider</label>
              <Input value="Resend" disabled className="font-mono text-xs" />
            </div>
            <div className="flex items-center gap-3 justify-end">
              <label className="text-xs text-[#AAB4A8]">Usar configuração manual</label>
              <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} data-testid="integ-cfg-enabled" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">API Key do Resend</label>
              {current?.has_api_key && <span className="text-[10px] text-[#AAB4A8]">atual: <code className="font-mono">{current?.api_key_mask}</code></span>}
            </div>
            <div className="relative">
              <Input type={showKey ? "text" : "password"} placeholder={current?.has_api_key ? "Deixe em branco para manter a chave atual" : "re_..."} value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} data-testid="integ-cfg-apikey" />
              <button type="button" onClick={() => setShowKey(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#AAB4A8] hover:text-white">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {current?.has_api_key && (
              <button type="button" className="text-xs text-red-400 hover:text-red-300 mt-1" onClick={() => setConfirmClear(true)} data-testid="integ-cfg-clear-key">
                Remover chave salva (voltar ao fallback de ambiente)
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Nome do remetente</label>
              <Input placeholder="TheoKratos RH" value={form.sender_name} onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))} data-testid="integ-cfg-sender-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">E-mail do remetente</label>
              <Input type="email" placeholder="contabil.consultoria@theokratos.com.br" value={form.sender_email} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))} data-testid="integ-cfg-sender-email" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">URL pública do sistema</label>
            <Input placeholder="https://theokratos.app" value={form.public_site_url} onChange={e => setForm(f => ({ ...f, public_site_url: e.target.value }))} data-testid="integ-cfg-url" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Webhook Secret (opcional)</label>
              {current?.has_webhook_secret && <span className="text-[10px] text-emerald-400">configurado</span>}
            </div>
            <div className="relative">
              <Input type={showWh ? "text" : "password"} placeholder={current?.has_webhook_secret ? "Deixe em branco para manter o secret atual" : "whsec_..."} value={form.webhook_secret} onChange={e => setForm(f => ({ ...f, webhook_secret: e.target.value }))} data-testid="integ-cfg-webhook" />
              <button type="button" onClick={() => setShowWh(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#AAB4A8] hover:text-white">
                {showWh ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="integ-cfg-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Salvar configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remover chave Resend salva?</AlertDialogTitle>
          <AlertDialogDescription>A chave será apagada do banco. O sistema voltará a usar RESEND_API_KEY das variáveis de ambiente (se existir).</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={clearKey}>Remover</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------
function EnvPanel({ env }) {
  const rows = [
    ["RESEND_API_KEY",            "Chave Resend"],
    ["SENDER_EMAIL",              "E-mail remetente"],
    ["SENDER_NAME",               "Nome do remetente"],
    ["PUBLIC_SITE_URL",           "URL pública"],
    ["RESEND_WEBHOOK_SECRET",     "Signing secret do webhook"],
    ["INTEGRATION_ENCRYPTION_KEY","Chave mestra de criptografia"],
  ];
  return (
    <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] overflow-hidden">
      <div className="p-4 text-xs text-[#AAB4A8] border-b border-[#2C3A2F]">
        Reflete apenas a <b>presença</b> de cada secret no runtime do backend. Nenhum valor é exposto.
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Variável</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([k, desc]) => (
            <TableRow key={k}>
              <TableCell className="font-mono text-xs">{k}</TableCell>
              <TableCell className="text-sm">{desc}</TableCell>
              <TableCell>
                {env[k]
                  ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" /> Presente</Badge>
                  : <Badge variant="outline" className="border-red-500/30 text-red-300"><AlertTriangle className="w-3 h-3 mr-1" /> Ausente</Badge>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------
function RecipientsPanel({ scope, companyId, isMaster, companies }) {
  const [items, setItems] = useState([]);
  const [useGlobalFallback, setUseGlobalFallback] = useState(true);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const canLoad = scope === "global" ? isMaster : !!companyId;

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = { scope, ...(scope === "company" ? { company_id: companyId } : {}) };
      const { data } = await api.get("/admin/email-recipients", { params });
      setItems(data.items || []);
      setUseGlobalFallback(data.use_global_fallback);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [scope, companyId, canLoad]);

  useEffect(() => { load(); }, [load]);

  const remove = async (rid) => {
    if (!window.confirm("Excluir destinatário?")) return;
    try { await api.delete(`/admin/email-recipients/${rid}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/admin/email-recipients/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTest = async (r) => {
    try {
      const ev = r.all_events ? "admissao" : (r.events[0] || "admissao");
      const { data } = await api.post("/admin/email-recipients/test", { recipient_id: r.id, event: ev });
      if (data.status === "sent") toast.success(`Enviado · ${data.resend_id}`);
      else toast.error(data.error || "Falhou");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFallback = async (v) => {
    try {
      await api.post("/admin/email-recipients/settings", { use_global_fallback: v });
      setUseGlobalFallback(v);
      toast.success(v ? "Fallback global ativado" : "Fallback global desativado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!canLoad) {
    return <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] p-6 text-sm text-[#AAB4A8]">Selecione uma empresa para gerenciar os destinatários do RH.</div>;
  }

  return (
    <div className="space-y-4" data-testid="recipients-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {isMaster && (
            <div className="flex items-center gap-2 text-xs text-[#AAB4A8]">
              <Switch checked={useGlobalFallback} onCheckedChange={toggleFallback} data-testid="recipients-fallback-switch" />
              <span>Usar destinatários globais como fallback</span>
            </div>
          )}
        </div>
        <Button onClick={() => setEditing({ email: "", name: "", active: true, all_events: false, events: [], kind: "to" })} data-testid="recipients-add-btn">+ Adicionar destinatário</Button>
      </div>

      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Eventos</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#C5A059]" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#AAB4A8]">Nenhum destinatário configurado.</TableCell></TableRow>
            ) : items.map(r => (
              <TableRow key={r.id} data-testid="recipient-row">
                <TableCell>{r.name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.email}</TableCell>
                <TableCell>
                  {r.all_events ? <Badge className="bg-[#C5A05920] text-[#C5A059] border-[#C5A05940]">Todos</Badge>
                                : <div className="flex flex-wrap gap-1">{r.events.map(e => <Badge key={e} variant="outline" className="text-[10px]">{events.find(x => x.key === e)?.label || e}</Badge>)}</div>}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] uppercase">{r.kind}</Badge></TableCell>
                <TableCell>
                  <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`recipient-active-${r.id}`} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => sendTest(r)} data-testid={`recipient-test-${r.id}`}><Send className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <RecipientEditor
          scope={scope} companyId={companyId}
          events={events} initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RecipientEditor({ scope, companyId, events, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    id: initial.id, name: initial.name || "", email: initial.email || "",
    active: initial.active !== false, all_events: !!initial.all_events,
    events: initial.events || [], kind: initial.kind || "to",
  });
  const [saving, setSaving] = useState(false);

  const toggleEvent = (k) => setF(x => ({
    ...x, events: x.events.includes(k) ? x.events.filter(e => e !== k) : [...x.events, k],
  }));

  const save = async () => {
    if (!f.email.includes("@")) { toast.error("E-mail inválido"); return; }
    if (!f.all_events && f.events.length === 0) { toast.error("Selecione pelo menos um evento ou marque \"todos\""); return; }
    setSaving(true);
    try {
      const body = { ...f, scope, company_id: scope === "company" ? companyId : null };
      if (f.id) await api.put(`/admin/email-recipients/${f.id}`, body);
      else      await api.post(`/admin/email-recipients`, body);
      toast.success("Salvo");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{f.id ? "Editar destinatário" : "Novo destinatário"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Nome</label>
              <Input value={f.name} onChange={e => setF(x => ({ ...x, name: e.target.value }))} data-testid="recipient-form-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">E-mail</label>
              <Input type="email" value={f.email} onChange={e => setF(x => ({ ...x, email: e.target.value }))} data-testid="recipient-form-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Tipo</label>
              <Select value={f.kind} onValueChange={v => setF(x => ({ ...x, kind: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="to">Para</SelectItem>
                  <SelectItem value="cc">CC</SelectItem>
                  <SelectItem value="bcc">CCO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={f.active} onCheckedChange={v => setF(x => ({ ...x, active: v }))} />
              <span className="text-xs text-[#AAB4A8]">{f.active ? "Ativo" : "Inativo"}</span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Eventos</label>
              <label className="flex items-center gap-2 text-xs text-[#AAB4A8] cursor-pointer">
                <Checkbox checked={f.all_events} onCheckedChange={v => setF(x => ({ ...x, all_events: !!v, events: v ? [] : x.events }))} data-testid="recipient-form-all" />
                Receber todas as notificações
              </label>
            </div>
            {!f.all_events && (
              <div className="grid grid-cols-2 gap-2">
                {events.map(ev => (
                  <label key={ev.key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded border cursor-pointer ${f.events.includes(ev.key) ? "bg-[#C5A05920] border-[#C5A05940] text-white" : "border-[#2C3A2F] text-[#AAB4A8]"}`}>
                    <Checkbox checked={f.events.includes(ev.key)} onCheckedChange={() => toggleEvent(ev.key)} />
                    {ev.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="recipient-form-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
